import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Every order-desk route hangs off the loan application, so the loan id is part
// of the path rather than a query param — it is the aggregate root the backend
// authorises against.
const servicesRoot = (applicationId) => `/b2b/loan/applications/${applicationId}/services`;

export const serviceOrderingAPI = createApi({
  reducerPath: 'serviceOrdering',
  tagTypes: ['OrderDesk', 'ServiceOrder', 'Vendors'],
  baseQuery: fetchBaseQuery({ baseUrl: api, credentials: 'include' }),
  endpoints: (builder) => ({
    // The order desk payload: every applicable service for the loan, whether or
    // not it has been ordered yet. Services with no order are what the "To order"
    // filter counts, so this cannot be an orders-only list.
    getOrderDesk: builder.query({
      query: ({ applicationId }) => ({
        url: servicesRoot(applicationId),
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['OrderDesk'],
    }),

    getServiceOrder: builder.query({
      query: ({ applicationId, orderId }) => ({
        url: `${servicesRoot(applicationId)}/orders/${orderId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: (result, error, arg) => [{ type: 'ServiceOrder', id: arg?.orderId }],
    }),

    placeServiceOrder: builder.mutation({
      query: ({ applicationId, ...body }) => ({
        url: `${servicesRoot(applicationId)}/orders`,
        method: 'POST',
        headers: getHeaders(),
        body,
      }),
      invalidatesTags: ['OrderDesk', 'ServiceOrder'],
    }),

    rushServiceOrder: builder.mutation({
      query: ({ applicationId, orderId, ...body }) => ({
        url: `${servicesRoot(applicationId)}/orders/${orderId}/rush`,
        method: 'POST',
        headers: getHeaders(),
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        'OrderDesk',
        { type: 'ServiceOrder', id: arg?.orderId },
      ],
    }),

    deliverServiceOrder: builder.mutation({
      query: ({ applicationId, orderId, ...body }) => ({
        url: `${servicesRoot(applicationId)}/orders/${orderId}/deliver`,
        method: 'POST',
        headers: getHeaders(),
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        'OrderDesk',
        { type: 'ServiceOrder', id: arg?.orderId },
      ],
    }),

    cancelServiceOrder: builder.mutation({
      query: ({ applicationId, orderId, ...body }) => ({
        url: `${servicesRoot(applicationId)}/orders/${orderId}/cancel`,
        method: 'POST',
        headers: getHeaders(),
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        'OrderDesk',
        { type: 'ServiceOrder', id: arg?.orderId },
      ],
    }),

    // "Chase vendor" on a past-ETA card. Escalation is an appended order event,
    // never an edit of an existing one — the order log is append-only.
    escalateServiceOrder: builder.mutation({
      query: ({ applicationId, orderId, ...body }) => ({
        url: `${servicesRoot(applicationId)}/orders/${orderId}/escalate`,
        method: 'POST',
        headers: getHeaders(),
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        'OrderDesk',
        { type: 'ServiceOrder', id: arg?.orderId },
      ],
    }),

    reorderService: builder.mutation({
      query: ({ applicationId, orderId, ...body }) => ({
        url: `${servicesRoot(applicationId)}/orders/${orderId}/reorder`,
        method: 'POST',
        headers: getHeaders(),
        body,
      }),
      invalidatesTags: ['OrderDesk', 'ServiceOrder'],
    }),

    // Vendor directory and the mortgagee clause for the Vendors & Templates view.
    getServiceVendors: builder.query({
      query: ({ applicationId }) => ({
        url: `${servicesRoot(applicationId)}/vendors`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['Vendors'],
    }),
  }),
});

export const {
  useGetOrderDeskQuery,
  useGetServiceOrderQuery,
  usePlaceServiceOrderMutation,
  useRushServiceOrderMutation,
  useDeliverServiceOrderMutation,
  useCancelServiceOrderMutation,
  useEscalateServiceOrderMutation,
  useReorderServiceMutation,
  useGetServiceVendorsQuery,
} = serviceOrderingAPI;
